/**
 * Per-contract change table ("Abu Sultan 2.0", tab option-change-tracker).
 *
 * Client for /api/edge/contract-changes (212 flow backend, subscription-gated like the rest
 * of /api/edge). The server joins the latest chain snapshot with the prior session's close,
 * one row per contract; this file only types and fetches it. The platform credential is
 * attached by the global fetch wrapper, so plain fetch is correct here.
 *
 * Any numeric field may be null = unavailable; the UI renders "—", never 0.
 */
import { getApiUrl } from "../config/api";
import { forceSessionExpired } from "../utils/authUtils";

export type Num = number | null;

export type ChangeMetric = "oi" | "volume" | "delta" | "gamma" | "theta" | "vega" | "iv" | "mid";

export const CHANGE_METRICS: readonly ChangeMetric[] = ["oi", "volume", "delta", "gamma", "theta", "vega", "iv", "mid"];

export interface ChangePair {
    now: Num;
    prior: Num;
    chg: Num;
    pct: Num;
}

export interface ContractChangeRow extends Record<ChangeMetric, ChangePair> {
    contract: string;
    expiry: string;
    strike: number;
    right: "C" | "P";
    bid: Num;
    ask: Num;
    prior_bid: Num;
    prior_ask: Num;
    has_prior: boolean;
}

export interface SideTotals {
    oi: ChangePair;
    volume: ChangePair;
}

export interface ContractChangesResponse {
    ok: true;
    symbol: string;
    expiries: string[];
    snapshot_ts: string;
    now_session: string;
    prior_session: string;
    spot: { now: Num; prior: Num };
    sources: Record<string, "snapshot" | "live">;
    prior_contracts: Record<string, number>;
    side: string;
    rows: ContractChangeRow[];
    summary: { contracts: number; matched: number; calls: SideTotals; puts: SideTotals };
}

export interface ExpiriesResponse {
    ok: boolean;
    symbol: string;
    expiries: string[];
    source: "snapshot" | "listed";
}

export const MAX_EXPIRIES = 6;

export class ContractChangesError extends Error {
    status: number;
    code: string;
    constructor(status: number, code: string) {
        super(code);
        this.status = status;
        this.code = code;
    }
}

async function getJson<T>(path: string, signal?: AbortSignal, timeoutMs = 120_000): Promise<T> {
    const ac = new AbortController();
    const onAbort = () => ac.abort();
    signal?.addEventListener("abort", onAbort);
    const timer = window.setTimeout(() => ac.abort(), timeoutMs);
    try {
        const res = await fetch(getApiUrl(path), {
            method: "GET",
            headers: { Accept: "application/json" },
            signal: ac.signal,
        });
        if (res.status === 401) {
            forceSessionExpired();
            throw new ContractChangesError(401, "session_ended");
        }
        let body: any = null;
        try {
            body = await res.json();
        } catch {
            /* non-JSON body */
        }
        if (!res.ok || !body || body.ok === false) {
            throw new ContractChangesError(res.status, String(body?.error || body?.detail || `http_${res.status}`));
        }
        return body as T;
    } catch (e) {
        if (e instanceof ContractChangesError) throw e;
        if (ac.signal.aborted && !signal?.aborted) throw new ContractChangesError(504, "timeout");
        throw e;
    } finally {
        window.clearTimeout(timer);
        signal?.removeEventListener("abort", onAbort);
    }
}

export function normaliseSymbol(raw: string): string {
    return String(raw || "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9._]/g, "")
        .slice(0, 12);
}

export function fetchChangeExpiries(symbol: string, signal?: AbortSignal): Promise<ExpiriesResponse> {
    return getJson<ExpiriesResponse>(
        `/api/edge/contract-changes/expiries?symbol=${encodeURIComponent(normaliseSymbol(symbol))}`,
        signal,
        60_000,
    );
}

export function fetchContractChanges(
    symbol: string,
    expiries: string[],
    signal?: AbortSignal,
): Promise<ContractChangesResponse> {
    const exps = [...expiries].sort().slice(0, MAX_EXPIRIES).join(",");
    return getJson<ContractChangesResponse>(
        `/api/edge/contract-changes?symbol=${encodeURIComponent(normaliseSymbol(symbol))}&expiries=${encodeURIComponent(exps)}`,
        signal,
    );
}
